import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileAlt,
  faFolder,
  faFilePdf,
  faFileImage,
  faFileExcel,
  faFileWord,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";

/**
 * Displays statistics about available documents
 */
const DocumentStats = ({ stats }) => {
  const { totalDocuments, totalGroups, extensionCounts, latestUpload } = stats;

  // Function to get the appropriate icon for file type
  const getFileTypeIcon = (fileType) => {
    switch (fileType.toLowerCase()) {
      case "pdf":
        return <FontAwesomeIcon icon={faFilePdf} className="text-red-500" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
        return <FontAwesomeIcon icon={faFileImage} className="text-blue-500" />;
      case "xls":
      case "xlsx":
      case "csv":
        return (
          <FontAwesomeIcon icon={faFileExcel} className="text-green-600" />
        );
      case "doc":
      case "docx":
        return <FontAwesomeIcon icon={faFileWord} className="text-blue-600" />;
      default:
        return <FontAwesomeIcon icon={faFileAlt} className="text-gray-500" />;
    }
  };

  // Get the most common file types (top 3)
  const topFileTypes = Object.entries(extensionCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return (
    <div className="flex flex-wrap gap-4 px-4 py-3 bg-gray-50 rounded-lg mb-4">
      {/* Total documents */}
      <div className="flex items-center">
        <div className="h-8 w-8 flex items-center justify-center rounded-full bg-blue-100 mr-2">
          <FontAwesomeIcon icon={faFileAlt} className="text-blue-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{totalDocuments}</p>
          <p className="text-xs text-gray-500">Documents</p>
        </div>
      </div>

      {/* Total categories */}
      <div className="flex items-center">
        <div className="h-8 w-8 flex items-center justify-center rounded-full bg-indigo-100 mr-2">
          <FontAwesomeIcon icon={faFolder} className="text-indigo-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-900">{totalGroups}</p>
          <p className="text-xs text-gray-500">Categories</p>
        </div>
      </div>

      {/* Latest upload */}
      {latestUpload && (
        <div className="flex items-center">
          <div className="h-8 w-8 flex items-center justify-center rounded-full bg-green-100 mr-2">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-green-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">
              {latestUpload.toLocaleDateString()}
            </p>
            <p className="text-xs text-gray-500">Latest Upload</p>
          </div>
        </div>
      )}

      {/* File type distribution */}
      <div className="flex flex-1 justify-end">
        <div className="flex items-center">
          <div className="text-xs text-gray-500 mr-2">Top file types:</div>
          <div className="flex gap-2">
            {topFileTypes.map(([ext, count]) => (
              <div
                key={ext}
                className="flex items-center bg-white px-2 py-1 rounded-full border border-gray-200"
              >
                <span className="mr-1">{getFileTypeIcon(ext)}</span>
                <span className="text-xs font-medium">{ext || "Unknown"}</span>
                <span className="text-xs text-gray-500 ml-1">({count})</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentStats;
